/**
 * LPA Audits API
 *
 * CRUD operations for `core.mod_lpa_audits`.
 * Audits are factory-scoped (tenant_id + factory_id) and represent
 * audit definitions tied to a specific factory location scope.
 *
 * Status lifecycle: draft → active → archived.
 * Only draft audits with no completions can be deleted.
 */

import { supabase } from '@/integrations/supabase/client';
import type {
  LpaAudit,
  LpaAuditWithDetails,
  CreateAuditInput,
  UpdateAuditInput,
} from '../types';

// ---------------------------------------------------------------------------
// Internal helpers
// ---------------------------------------------------------------------------

/**
 * Resolves owner profiles for a set of audits.
 * Returns a map of owner_id → profile data.
 */
async function resolveOwnerProfiles(
  ownerIds: string[],
): Promise<
  Record<
    string,
    { id: string; full_name: string | null; email: string; avatar_url: string | null }
  >
> {
  if (ownerIds.length === 0) return {};

  const uniqueIds = [...new Set(ownerIds)];

  const { data, error } = await supabase
    .schema('core')
    .from('profiles')
    .select('id, full_name, email, avatar_url')
    .in('id', uniqueIds);

  if (error) throw error;

  const map: Record<
    string,
    { id: string; full_name: string | null; email: string; avatar_url: string | null }
  > = {};
  for (const p of data ?? []) {
    map[p.id] = p as {
      id: string;
      full_name: string | null;
      email: string;
      avatar_url: string | null;
    };
  }
  return map;
}

/**
 * Resolves schedule counts for a set of audit IDs.
 * Returns a map of audit_id → count.
 */
async function resolveScheduleCounts(
  auditIds: string[],
): Promise<Record<string, number>> {
  if (auditIds.length === 0) return {};

  const { data, error } = await supabase
    .schema('core')
    .from('mod_lpa_schedules')
    .select('audit_id')
    .in('audit_id', auditIds);

  if (error) throw error;

  const counts: Record<string, number> = {};
  for (const row of data ?? []) {
    counts[row.audit_id] = (counts[row.audit_id] ?? 0) + 1;
  }
  return counts;
}

/**
 * Resolves location names (area, line, station) for a set of audits.
 * Returns maps of id → name for each level.
 */
async function resolveLocationNames(
  audits: LpaAudit[],
): Promise<{
  areas: Record<string, string>;
  lines: Record<string, string>;
  stations: Record<string, string>;
}> {
  const areaIds = [...new Set(audits.map((a) => a.area_id).filter(Boolean))] as string[];
  const lineIds = [...new Set(audits.map((a) => a.line_id).filter(Boolean))] as string[];
  const stationIds = [...new Set(audits.map((a) => a.station_id).filter(Boolean))] as string[];

  const [areaData, lineData, stationData] = await Promise.all([
    areaIds.length > 0
      ? supabase
          .schema('core')
          .from('factory_areas')
          .select('id, name')
          .in('id', areaIds)
          .then(({ data, error }) => {
            if (error) throw error;
            return data ?? [];
          })
      : Promise.resolve([]),
    lineIds.length > 0
      ? supabase
          .schema('core')
          .from('production_lines')
          .select('id, name')
          .in('id', lineIds)
          .then(({ data, error }) => {
            if (error) throw error;
            return data ?? [];
          })
      : Promise.resolve([]),
    stationIds.length > 0
      ? supabase
          .schema('core')
          .from('stations')
          .select('id, name')
          .in('id', stationIds)
          .then(({ data, error }) => {
            if (error) throw error;
            return data ?? [];
          })
      : Promise.resolve([]),
  ]);

  const areas: Record<string, string> = {};
  for (const a of areaData) areas[a.id] = a.name;

  const lines: Record<string, string> = {};
  for (const l of lineData) lines[l.id] = l.name;

  const stations: Record<string, string> = {};
  for (const s of stationData) stations[s.id] = s.name;

  return { areas, lines, stations };
}

// ---------------------------------------------------------------------------
// Queries
// ---------------------------------------------------------------------------

/**
 * Fetch all audits for a factory, with owner profiles and schedule counts.
 * Returns newest first.
 */
export async function fetchAudits(
  factoryId: string,
): Promise<LpaAuditWithDetails[]> {
  const { data, error } = await supabase
    .schema('core')
    .from('mod_lpa_audits')
    .select('*')
    .eq('factory_id', factoryId)
    .order('created_at', { ascending: false });

  if (error) throw error;
  const audits = (data ?? []) as unknown as LpaAudit[];

  // Resolve related data in parallel
  const [ownerMap, scheduleCounts, locationNames] = await Promise.all([
    resolveOwnerProfiles(audits.map((a) => a.owner_id)),
    resolveScheduleCounts(audits.map((a) => a.id)),
    resolveLocationNames(audits),
  ]);

  return audits.map((a) => ({
    ...a,
    owner: ownerMap[a.owner_id] ?? null,
    schedule_count: scheduleCounts[a.id] ?? 0,
    area_name: a.area_id ? (locationNames.areas[a.area_id] ?? null) : null,
    line_name: a.line_id ? (locationNames.lines[a.line_id] ?? null) : null,
    station_name: a.station_id ? (locationNames.stations[a.station_id] ?? null) : null,
  }));
}

/**
 * Fetch a single audit by ID, with owner profile and schedule count.
 */
export async function fetchAuditById(
  auditId: string,
): Promise<LpaAuditWithDetails | null> {
  const { data, error } = await supabase
    .schema('core')
    .from('mod_lpa_audits')
    .select('*')
    .eq('id', auditId)
    .maybeSingle();

  if (error) throw error;
  if (!data) return null;

  const audit = data as unknown as LpaAudit;

  const [ownerMap, scheduleCounts, locationNames] = await Promise.all([
    resolveOwnerProfiles([audit.owner_id]),
    resolveScheduleCounts([audit.id]),
    resolveLocationNames([audit]),
  ]);

  return {
    ...audit,
    owner: ownerMap[audit.owner_id] ?? null,
    schedule_count: scheduleCounts[audit.id] ?? 0,
    area_name: audit.area_id ? (locationNames.areas[audit.area_id] ?? null) : null,
    line_name: audit.line_id ? (locationNames.lines[audit.line_id] ?? null) : null,
    station_name: audit.station_id ? (locationNames.stations[audit.station_id] ?? null) : null,
  };
}

// ---------------------------------------------------------------------------
// Mutations
// ---------------------------------------------------------------------------

/** Create a new audit. Defaults to 'draft' status. */
export async function createAudit(
  input: CreateAuditInput,
): Promise<LpaAudit> {
  const { data, error } = await supabase
    .schema('core')
    .from('mod_lpa_audits')
    .insert({
      tenant_id: input.tenant_id,
      factory_id: input.factory_id,
      name: input.name,
      description: input.description ?? null,
      owner_id: input.owner_id,
      area_id: input.area_id ?? null,
      line_id: input.line_id ?? null,
      station_id: input.station_id ?? null,
      created_by: input.created_by ?? null,
    })
    .select()
    .single();

  if (error) throw error;
  return data as unknown as LpaAudit;
}

/** Update an existing audit. Only provided fields are changed. */
export async function updateAudit(
  auditId: string,
  input: UpdateAuditInput,
): Promise<LpaAudit> {
  const { data, error } = await supabase
    .schema('core')
    .from('mod_lpa_audits')
    .update(input)
    .eq('id', auditId)
    .select()
    .single();

  if (error) throw error;
  return data as unknown as LpaAudit;
}

/**
 * Delete an audit.
 *
 * Should only be called for draft audits with zero completions.
 * The caller (UI/hook) is responsible for checking preconditions;
 * DB foreign key constraints will prevent deletion if schedules/instances
 * reference this audit.
 */
export async function deleteAudit(auditId: string): Promise<void> {
  const { error } = await supabase
    .schema('core')
    .from('mod_lpa_audits')
    .delete()
    .eq('id', auditId);

  if (error) throw error;
}
